// Utilities for reading and manipulating URL/Location search params.

// Read a param from a Location's `search`. Ignores hash queries; returns null
// when absent.
export function getSearchParam(key: string, source: Location): string | null {
  return new URLSearchParams(source.search).get(key)
}

// Return a new URL with the given params appended (not overwritten). Falsy values
// are skipped.
export function appendSearchParams(
  url: URL | string,
  params: Record<string, string | undefined>
): URL {
  const result = new URL(url)
  for (const [key, value] of Object.entries(params)) {
    if (value) {
      result.searchParams.append(key, value)
    }
  }
  return result
}

// Delete the named params from a URL in place and return it.
export function scrubSearchParams(url: URL, paramNames: string[]): URL {
  paramNames.forEach((name) => url.searchParams.delete(name))
  return url
}

// Keep only the named params on a URL in place and return it.
export function keepSearchParams(url: URL, paramNames: string[]): URL {
  const keep = new Set(paramNames)
  for (const key of [...url.searchParams.keys()]) {
    if (!keep.has(key)) {
      url.searchParams.delete(key)
    }
  }
  return url
}

// Scrub the named params from `target`'s location via history.replaceState.
// No-op when none are present. `target` is explicit to avoid mutating a global window.
export function scrubSearchParamsFromLocation(
  paramNames: string[],
  target: Window
): void {
  const url = new URL(target.location.href)
  if (!paramNames.some((name) => url.searchParams.has(name))) {
    return
  }
  scrubSearchParams(url, paramNames)
  target.history.replaceState(null, "", url.toString())
}
